/**
 * Thumbnail Component
 */

import { getImagePathForSize } from '../../../../utils/image';
import { compareResponsiveImage } from '../../../../utils/track';
import './styles.scss';

import * as classNames from 'classnames';
import { isEqual, isEqualWith } from 'lodash';
import * as React from 'react';
import * as BEM from 'react-bem-helper';

import ImageLoaded, { IImageLoadedRenderProps } from '../../../../containers/ImageLoaded';

interface IOwnProps extends IHyperlinkProps {
  className?: string;
  ratio?: 'square' | 'four-thirds';
  responsive?: boolean;
  responsiveAssets?: IResponsiveImage;
  shape?: 'square' | 'circle';
  size?: ThumbnailSize | number;
  src?: string | null;
  withShadow?: boolean;
  onClick?(e: React.MouseEvent<HTMLDivElement>): void;
}

export type ThumbnailProps = IOwnProps & IImageLoadedRenderProps;

const classes = new BEM('thumbnail');

class ThumbnailBase extends React.Component<ThumbnailProps> {
  static defaultProps: Partial<ThumbnailProps> = {
    ratio: 'square',
    shape: 'square',
  };

  shouldComponentUpdate(nextProps: ThumbnailProps) {
    return nextProps.className !== this.props.className
      || nextProps.imageLoaded !== this.props.imageLoaded
      || nextProps.ratio !== this.props.ratio
      || nextProps.responsive !== this.props.responsive
      || nextProps.shape !== this.props.shape
      || nextProps.size !== this.props.size
      || nextProps.src !== this.props.src
      || nextProps.withShadow !== this.props.withShadow
      || `${nextProps.onClick}` !== `${this.props.onClick}`
      || !isEqual(nextProps.hyperLinkProps, this.props.hyperLinkProps)
      || !isEqualWith(nextProps.responsiveAssets, this.props.responsiveAssets, compareResponsiveImage);
  }

  componentDidUpdate(prevProps: ThumbnailProps) {
    if (!isEqualWith(prevProps.responsiveAssets, this.props.responsiveAssets, compareResponsiveImage)) {
      this.props.reloadImage();
    }
  }

  render() {
    const {
      responsiveAssets,
      children,
      className,
      hyperLinkProps,
      imageLoaded,
      onClick,
      src,
      ratio,
      responsive,
      shape,
      size,
      withShadow,
    } = this.props;

    const classModifiers = classNames(
      responsive && 'is-responsive',
      imageLoaded ? 'is-loaded' : 'loading',
      typeof size === 'string' && size,
    );

    const extraClasses = classNames(
      className,
      `ratio--${ratio}`,
      `shape--${shape}`,
      withShadow && imageLoaded && 'with-shadow',
      !!onClick && 'clickable',
    );

    const InnerWrapperComponent: string = hyperLinkProps ? 'a' : 'div';
    const innerProps: any = {};

    if (onClick) {
      innerProps.onClick = onClick;
    } else if (hyperLinkProps) {
      innerProps.href = hyperLinkProps.href;
      if (hyperLinkProps.external) {
        innerProps.target = '_blank';
      }
    }

    if (src) {
      innerProps.style = {
        backgroundImage: `url(${src})`,
      };
    } else if (responsiveAssets) {
      innerProps.style = {
        backgroundImage: `url(${getImagePathForSize(responsiveAssets, typeof size === 'string' ? size : 'large')})`,
      };
    }

    return (
      <div
        style={typeof size === 'number' ? { width: size } : undefined}
        {...classes('', classModifiers, extraClasses) }
      >
        <InnerWrapperComponent {...innerProps} {...classes('inner') } />
        {children}
      </div>
    );
  }
}

const Thumbnail: React.SFC<IOwnProps> = (props) => (
  <ImageLoaded background=".thumbnail__inner">
    {(imageLoadedProps) => <ThumbnailBase {...imageLoadedProps} {...props} />}
  </ImageLoaded>
);

export default Thumbnail;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/public/components/atoms/images/Thumbnail/index.tsx